import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import moment from 'moment';

import { AppraisalInterviewStatusesEnum } from '@thinknet-gmbh/platform-common';
import {
  Form,
  TextAreaInput,
  TextInput,
  SelectInput,
  DatePickerInput,
  Separator,
  Icon,
  ICONS,
  PLATFORM_THEME_COLORS,
} from '@thinknet-gmbh/platform-common-ui';

import { useGetCurrentAccount } from 'src/hooks/accounts/get-current-account.hook';

import { IAppraisalFormElementProps } from './employee-interview-edit-form-elements.interfaces';
import { getFormItemName } from './employee-interview-edit-form-elements.helpers';
import { FormContent, FormElementsWrapper } from './employee-interview-edit-form-elements.styles';
import { InterviewArticlesFormList } from './components/employee-interview-articles-form-list';

const EmployeeInterviewEditFormElements = ({
  field,
  remove,
  interviewTypeId,
  initialValues,
  interviewTypesList,
  form,
}: IAppraisalFormElementProps) => {
  const { t, i18n } = useTranslation(['common', 'employee']);
  const [selectedInterviewId, setSelectedInterviewId] = useState(null);
  const [isReset, setIsReset] = useState(false);
  const { profile } = useGetCurrentAccount();

  const interviewType = interviewTypesList?.find(
    (item: any) => item._id === (selectedInterviewId || interviewTypeId)
  );

  useEffect(() => {
    if (form && field && isReset) {
      const { interviews } = form.getFieldsValue();
      Object.assign(interviews[field.name], {
        interviewArticles: interviewType.fieldsHeadlines,
      });
      form.setFieldsValue({ interviews });
      setIsReset(false);
    }
  }, [isReset, form, field, interviewType]);

  return (
    <>
      {field && <Separator />}
      <FormElementsWrapper>
        <div>
          <FormContent isField={!!field}>
            {field && (
              <Form.Item
                name={[field.name, 'interviewTypeId']}
                label={t('employee:employeeInterview.labels.interviewType')}
                initialValue={interviewTypeId}
                rules={[
                  {
                    required: true,
                    message: t('employee:employeeInterview.validation.interviewTypeRequired'),
                  },
                ]}
              >
                <SelectInput
                  onSelect={id => {
                    setSelectedInterviewId(id);
                    setIsReset(true);
                  }}
                >
                  {interviewTypesList?.map(({ _id, name }: any) => (
                    <SelectInput.Option value={_id} key={_id}>
                      {name}
                    </SelectInput.Option>
                  ))}
                </SelectInput>
              </Form.Item>
            )}
            <Form.Item
              name={getFormItemName({ field, name: 'interviewName' })}
              label={t('employee:employeeInterview.labels.employeeInterviewName')}
            >
              <TextInput placeholder="Bill Parker" maxLength={100} />
            </Form.Item>
            <Form.Item label={t('common:labels.creationDate')} required>
              <DatePickerInput
                lang={i18n.language}
                format="DD.MM.YYYY"
                disabled
                defaultValue={moment(initialValues?.createdAt) || moment()}
              />
            </Form.Item>

            <Form.Item
              name={getFormItemName({ field, name: 'setTo' })}
              label={t('employee:employeeInterview.labels.setTo')}
              rules={[{ required: true, message: t('employee:validationError.dateRequired') }]}
            >
              <DatePickerInput lang={i18n.language} format="DD.MM.YYYY" />
            </Form.Item>
            <Form.Item label={t('employee:labels.createdBy')} required>
              <TextInput
                disabled
                defaultValue={
                  initialValues?.createdByMember.profile.displayName || profile.displayName
                }
              />
            </Form.Item>

            <Form.Item
              name={getFormItemName({ field, name: 'status' })}
              label={t('common:labels.status')}
              rules={[{ required: true }]}
            >
              <SelectInput>
                {Object.keys(AppraisalInterviewStatusesEnum).map(status => (
                  <SelectInput.Option value={status} key={status}>
                    {t(`employee:employeeInterview.statuses.${status.toLowerCase()}`)}
                  </SelectInput.Option>
                ))}
              </SelectInput>
            </Form.Item>
            <Form.Item
              name={getFormItemName({ field, name: 'feedback' })}
              label={t('employee:employeeInterview.labels.feedback')}
            >
              <TextAreaInput
                showCount
                maxLength={500}
                placeholder={t('employee:labels.writeDetails')}
              />
            </Form.Item>
          </FormContent>
          <InterviewArticlesFormList
            t={t}
            formListName={getFormItemName({ field, name: 'interviewArticles' })}
            interviewType={
              initialValues?.interviewArticles ||
              interviewType.fieldsHeadlines?.map((headline: any) => ({
                headline,
                description: undefined,
              }))
            }
          />
        </div>
        {field && (
          <Icon
            icon={ICONS.trash}
            size={22}
            rightMargin={false}
            color={PLATFORM_THEME_COLORS.tone.red}
            onClick={() => remove && remove(field.name)}
          />
        )}
      </FormElementsWrapper>
    </>
  );
};

export default EmployeeInterviewEditFormElements;
